/**
 * CycleSettingsPanel
 *
 * Right-side slide-over for creating a new cycle or editing an existing one.
 *
 * CREATE mode: the one create-cycle form (CreateCycleDialog, owner ruling #7)
 *   — same fields, submit and follow-up as every other place a cycle is started.
 *
 * EDIT mode: edit name, description, workflow + lifecycle actions
 *   (activate, pause, complete, archive).
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, RefreshCcw, Play, Pause, CheckCircle, Archive,
  Loader2, AlertCircle, Target,
} from 'lucide-react';
import { useDegapProcessStore } from '@/stores/prioritization/degapProcessStore';
import { useWorkflowStore } from '@/stores/prioritization/workflowStore';
import { useStrategyStore } from '@/stores/prioritization/strategyStore';
import { type StrategyRef } from '@/types/prioritization/degapCycle';
import { FEATURE_FLAGS } from '@/lib/featureFlags';
import { getWorkspaceModules, WORKSPACE_MODULES_UPDATED_EVENT, isModuleFeatureAccessible } from '@/lib/workspaceModulesService';
import { getCurrentWorkspaceId } from '@/lib/supabaseSync';
import { toFriendlyDbError } from '@/lib/friendlyDbError';
import { CreateCycleDialog } from '@/components/prioritization/cycles/CreateCycleDialog';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface Props {
  mode: 'create' | 'edit';
  cycleId?: string;
  open: boolean;
  onClose: () => void;
  onCreated?: (cycleId: string) => void;
}

// ─── Status lifecycle actions ──────────────────────────────────────────────────

function statusLabel(status: string) {
  const map: Record<string, { label: string; color: string }> = {
    draft:     { label: 'Draft',     color: 'bg-[var(--hover-tint)] text-[var(--text-sec)]' },
    active:    { label: 'Active',    color: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' },
    paused:    { label: 'Paused',    color: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' },
    completed: { label: 'Complete',  color: 'bg-[var(--slate-dim)] text-[var(--text-sec)]' },
    archived:  { label: 'Archived',  color: 'bg-[var(--hover-tint)] text-[var(--text-ter)]' },
  };
  const cfg = map[status] ?? { label: status, color: 'bg-[var(--hover-tint)] text-[var(--text-ter)]' };
  return (
    <span className={`text-xs font-semibold rounded-full px-2.5 py-0.5 ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

// ─── Component ─────────────────────────────────────────────────────────────────

export function CycleSettingsPanel({ mode, cycleId, open, onClose, onCreated }: Props) {
  const navigate = useNavigate();
  const workspaceId = getCurrentWorkspaceId();
  const [workspaceFeatures, setWorkspaceFeatures] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (!workspaceId) return;
    getWorkspaceModules(workspaceId).then(({ data }) => setWorkspaceFeatures(data));
    const handler = () => { if (workspaceId) getWorkspaceModules(workspaceId).then(({ data }) => setWorkspaceFeatures(data)); };
    window.addEventListener(WORKSPACE_MODULES_UPDATED_EVENT, handler);
    return () => window.removeEventListener(WORKSPACE_MODULES_UPDATED_EVENT, handler);
  }, [workspaceId]);

  // Store actions
  const updateCycle = useDegapProcessStore(s => s.updateCycle);
  const startCycle  = useDegapProcessStore(s => s.startCycle);
  const resumeCycle = useDegapProcessStore(s => s.resumeCycle);
  const pauseCycle  = useDegapProcessStore(s => s.pauseCycle);
  const completeCycle = useDegapProcessStore(s => s.completeCycle);
  const getCycle = useDegapProcessStore(s => s.getCycle);
  const fetchWorkflows = useWorkflowStore(s => s.fetchWorkflows);
  const workflows = useWorkflowStore(s => s.workflows);

  const cycle = cycleId ? getCycle(cycleId) : undefined;

  // Strategy store
  const getAllStrategies = useStrategyStore(s => s.getAllStrategies);
  const fetchStrategies = useStrategyStore(s => s.fetchFromSupabase);

  // Show strategy section only if both the workspace module and the individual flag are enabled
  const showStrategySection = useMemo(
    () => isModuleFeatureAccessible(workspaceFeatures, 'strategy', FEATURE_FLAGS.STRATEGY_NAV)
       || isModuleFeatureAccessible(workspaceFeatures, 'strategy_kernel', FEATURE_FLAGS.STRATEGY_KERNEL_NAV),
    [workspaceFeatures]
  );

  // ── Form state ───────────────────────────────────────────────────────────────
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [selectedStrategyId, setSelectedStrategyId] = useState<string>('');
  const [manualStrategyName, setManualStrategyName] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lifecycleLoading, setLifecycleLoading] = useState<string | null>(null);

  // Populate from existing cycle when in edit mode
  useEffect(() => {
    if (mode === 'edit' && cycle) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const c = cycle as any;
      setName(cycle.name ?? '');
      setDescription(c.description ?? '');
      setWorkflowId(c.workflowId ?? null);
      const ref = c.strategyRef as StrategyRef | undefined;
      setSelectedStrategyId(ref?.id ?? '');
      setManualStrategyName(ref?.id ? '' : (ref?.name ?? ''));
    }
  }, [mode, cycle, open]);

  // Load workflows and strategies when the (edit) panel opens
  useEffect(() => {
    if (open && mode === 'edit') {
      fetchWorkflows();
      if (showStrategySection) fetchStrategies();
    }
  }, [open, mode, fetchWorkflows, fetchStrategies, showStrategySection]);

  // Build a StrategyRef from current form state
  const buildStrategyRef = (): StrategyRef | undefined => {
    if (selectedStrategyId) {
      const found = getAllStrategies().find(s => s.id === selectedStrategyId);
      if (found) return { id: found.id, name: found.name, source: 'internal' };
    }
    const manual = manualStrategyName.trim();
    if (manual) return { name: manual, source: 'manual' };
    return undefined;
  };

  // ── Save edits handler ───────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!cycleId) return;
    if (!name.trim()) { setError('Cycle name is required.'); return; }

    setSaving(true);
    setError(null);
    try {
      const result = await updateCycle(cycleId, {
        name: name.trim(),
        description: description.trim() || undefined,
        workflowId: workflowId ?? undefined,
        strategyRef: buildStrategyRef(),
      });
      if (!result.success) {
        setError(result.error ? toFriendlyDbError(result.error, 'CycleSettingsPanel.handleSave') : 'Update failed.');
      } else {
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? toFriendlyDbError(err.message, 'CycleSettingsPanel.handleSave') : 'An error occurred.');
    } finally {
      setSaving(false);
    }
  };

  // ── Lifecycle action handler ─────────────────────────────────────────────────
  const handleLifecycle = async (action: 'activate' | 'resume' | 'pause' | 'complete') => {
    if (!cycleId) return;
    setLifecycleLoading(action);
    setError(null);
    try {
      if (action === 'activate') {
        const started = await startCycle(cycleId);
        if (!started.ok) setError(started.error ?? 'The cycle could not be started.');
      }
      if (action === 'resume')   await resumeCycle(cycleId);
      if (action === 'pause')    await pauseCycle(cycleId);
      if (action === 'complete') await completeCycle(cycleId);
    } catch (err) {
      setError(err instanceof Error ? toFriendlyDbError(err.message, 'CycleSettingsPanel.handleLifecycle') : 'Action failed.');
    } finally {
      setLifecycleLoading(null);
    }
  };

  if (!open) return null;

  // Create mode is the one create-cycle form (owner ruling #7) — the same
  // dialog every other place a cycle is started uses.
  if (mode === 'create') {
    return <CreateCycleDialog open onClose={onClose} onCreated={onCreated} />;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentStatus = (cycle as any)?.status ?? 'draft';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40 transition-opacity"
        onClick={onClose}
        aria-hidden
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-[var(--bg-raised)] shadow-xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)]">
          <div className="flex items-center gap-2">
            <RefreshCcw className="h-4 w-4 text-[var(--text-ter)]" />
            <h2 className="font-semibold text-[var(--text-pri)]">
              Cycle Settings
            </h2>
            {statusLabel(currentStatus)}
          </div>
          <button
            onClick={onClose}
            className="text-[var(--text-ter)] hover:text-[var(--text-sec)] p-1 hover:bg-[var(--hover-tint)] transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 bg-[var(--coral-dim)] border border-[var(--coral)] px-3 py-2 text-sm text-[var(--coral)]">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* ── Name ─────────────────────────────────────────────────────── */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Name <span className="text-[var(--coral)]">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g., Q2 2026 Product Priorities"
              className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            />
          </div>

          {/* ── Description ─────────────────────────────────────────────── */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Description <span className="text-[var(--text-ter)] font-normal text-xs">(optional)</span>
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What is this prioritization effort for?"
              rows={3}
              className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none resize-none"
            />
          </div>

          {/* ── Workflow ─────────────────────────────────────────────────── */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Workflow</label>
            <select
              value={workflowId ?? ''}
              onChange={e => setWorkflowId(e.target.value || null)}
              className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 text-sm border border-[var(--rule)] focus:ring-2 focus:ring-indigo-500 outline-none"
              aria-label="Cycle workflow"
            >
              <option value="">Use workspace default</option>
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {(workflows ?? []).filter((w: any) => w.status !== 'archived').map((w: any) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-[var(--text-ter)]">
              Governs result resolution, Catchball, and session rules.
            </p>
          </div>

          {/* ── Strategy Connection (optional, visible when strategy modules are on) */}
          {showStrategySection && (() => {
            const strategies = getAllStrategies();
            return (
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1 flex items-center gap-1.5">
                  <Target className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                  Strategy
                  <span className="text-[var(--text-ter)] font-normal text-xs">(optional)</span>
                </label>
                {strategies.length > 0 ? (
                  <select
                    value={selectedStrategyId}
                    onChange={e => {
                      setSelectedStrategyId(e.target.value);
                      if (e.target.value) setManualStrategyName('');
                    }}
                    className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 text-sm border border-[var(--rule)] focus:ring-2 focus:ring-indigo-500 outline-none"
                    aria-label="Link to strategy"
                  >
                    <option value="">— None —</option>
                    {strategies.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                    <option value="__manual__" disabled>─ or type a name below ─</option>
                  </select>
                ) : null}
                {(!selectedStrategyId || strategies.length === 0) && (
                  <input
                    type="text"
                    value={manualStrategyName}
                    onChange={e => setManualStrategyName(e.target.value)}
                    placeholder="e.g., 2026 Growth Strategy"
                    className={`w-full px-3 py-2 text-sm border border-[var(--rule)]  focus:ring-2 focus:ring-indigo-500 outline-none ${strategies.length > 0 ? 'mt-2' : ''}`}
                  />
                )}
                <p className="mt-1 text-xs text-[var(--text-ter)]">
                  Connects this cycle to an upstream strategy for alignment tracking.
                </p>
              </div>
            );
          })()}

          {/* ── Lifecycle actions (edit only) ──────────────────────────── */}
          {mode === 'edit' && cycle && (
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Lifecycle</label>
              <div className="space-y-2">
                {currentStatus === 'draft' && (
                  <button
                    onClick={() => handleLifecycle('activate')}
                    disabled={!!lifecycleLoading}
                    className="w-full flex items-center gap-2 px-4 py-2.5 border border-[var(--green)] bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] text-sm font-medium hover:bg-[var(--green-dim)] transition-colors disabled:opacity-50"
                  >
                    {lifecycleLoading === 'activate'
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <Play className="h-4 w-4" />}
                    Activate Cycle
                  </button>
                )}
                {currentStatus === 'active' && (
                  <button
                    onClick={() => handleLifecycle('pause')}
                    disabled={!!lifecycleLoading}
                    className="w-full flex items-center gap-2 px-4 py-2.5 border border-[var(--mustard)] bg-[var(--mustard-dim)] text-[var(--mustard)] text-sm font-medium hover:bg-[var(--mustard-dim)] transition-colors disabled:opacity-50"
                  >
                    {lifecycleLoading === 'pause'
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <Pause className="h-4 w-4" />}
                    Pause Cycle
                  </button>
                )}
                {(currentStatus === 'active' || currentStatus === 'paused') && (
                  <button
                    onClick={() => handleLifecycle('complete')}
                    disabled={!!lifecycleLoading}
                    className="w-full flex items-center gap-2 px-4 py-2.5 border border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)] text-sm font-medium hover:bg-[var(--slate-dim)] transition-colors disabled:opacity-50"
                  >
                    {lifecycleLoading === 'complete'
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <CheckCircle className="h-4 w-4" />}
                    Mark Complete
                  </button>
                )}
                {currentStatus === 'paused' && (
                  <button
                    onClick={() => handleLifecycle('resume')}
                    disabled={!!lifecycleLoading}
                    className="w-full flex items-center gap-2 px-4 py-2.5 border border-[var(--green)] bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] text-sm font-medium hover:bg-[var(--green-dim)] transition-colors disabled:opacity-50"
                  >
                    {lifecycleLoading === 'resume'
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <Play className="h-4 w-4" />}
                    Resume Cycle
                  </button>
                )}
                {(currentStatus === 'completed' || currentStatus === 'active') && (
                  <button
                    onClick={() => navigate(`/prioritization/cycles/${cycleId}`)}
                    className="w-full flex items-center gap-2 px-4 py-2.5 border border-[var(--rule)] text-[var(--text-ter)] text-sm font-medium hover:bg-[var(--surface)] transition-colors"
                  >
                    <Archive className="h-4 w-4" />
                    Archive (from cycle detail)
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[var(--rule)] flex items-center justify-between gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="flex items-center gap-2 px-5 py-2 bg-[var(--slate)] text-white text-sm font-medium hover:bg-[var(--slate)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save Changes
          </button>
        </div>
      </div>
    </>
  );
}
